// @flow
// Keep in sync with mobile version
import React, { Component } from 'react';
import type { ValidColors } from 'app/common/react/components/growth/activation/animations/DelightfulBubbles/DelightfulBubblesStyles';
import { Box } from 'gestalt';
import {
  BUBBLE_COLORS,
  DELIGHTFUL_BUBBLES_KEYFRAMES,
  DelightfulBubblesStyles,
} from 'app/common/react/components/growth/activation/animations/DelightfulBubbles/DelightfulBubblesStyles';
import {
  getRandomCoordsOnEllipse,
  getRandomInt,
} from 'app/common/react/components/growth/activation/animations/animationsUtils';

type DelightfulBubblesProps = {|
  bubbleSize: number, // pixels
  children: any,
  delayFactor: number, // Time between individual bubbles (seconds)
  delayOffset: number, // Delay for entire animation (seconds)
  height?: number,
  numBubbles: number,
  width?: number,
  xRadiusAdjustment: number, // pixels
  yRadiusAdjustment: number, // pixels
|};

type DelightfulBubblesState = {|
  isInitialized: boolean,
|};

type BubbleProps = {|
  delay: number,
  diameter: number,
  height: number,
  initialColor: ValidColors,
  width: number,
  xOffset: number,
  yOffset: number,
|};

const Bubble = (props: BubbleProps) => {
  const { delay, diameter, height, initialColor, width, xOffset, yOffset } = props;
  const radius = Math.floor(diameter / 2);
  const styles = DelightfulBubblesStyles(
    delay,
    diameter,
    height,
    initialColor,
    radius,
    width,
    xOffset,
    yOffset,
  );
  return (
    <Box dangerouslySetInlineStyle={{ __style: styles.bubblePositionStyles }}>
      <Box dangerouslySetInlineStyle={{ __style: styles.bubbleSwirlStyles }}>
        <Box dangerouslySetInlineStyle={{ __style: styles.bubbleGrowShrinkStyles }}>
          <Box dangerouslySetInlineStyle={{ __style: styles.bubbleShapeStyles }} />
        </Box>
      </Box>
    </Box>
  );
};

class DelightfulBubbles extends Component<DelightfulBubblesProps, DelightfulBubblesState> {
  static defaultProps = {
    bubbleSize: 16,
    numBubbles: 10,
    xRadiusAdjustment: 0,
    yRadiusAdjustment: 0,
    delayFactor: 1,
    delayOffset: 0,
  };

  state: DelightfulBubblesState = {
    isInitialized: false,
  };

  componentDidMount() {
    this.requestAnimationFrameId = window.requestAnimationFrame(() => {
      this.setState({ isInitialized: true });
    });
  }

  shouldComponentUpdate(nextProps: DelightfulBubblesProps, nextState: DelightfulBubblesState) {
    return (
      !nextProps.numBubbles === this.props.numBubbles ||
      !nextState.isInitialized === this.state.isInitialized
    );
  }

  getSize() {
    if (typeof this.props.width !== 'undefined' && typeof this.props.height !== 'undefined') {
      return { height: this.props.height, width: this.props.width };
    } else if (this.containerElement) {
      return {
        height: this.containerElement.offsetHeight,
        width: this.containerElement.offsetWidth,
      };
    } else {
      return {};
    }
  }

  containerElement: ?HTMLDivElement;
  requestAnimationFrameId: number;

  createBubbles(height: number, width: number) {
    const {
      bubbleSize,
      delayFactor,
      delayOffset,
      numBubbles,
      xRadiusAdjustment,
      yRadiusAdjustment,
    } = this.props;
    const ellipseHeight = height + yRadiusAdjustment * 2;
    const ellipseWidth = width + xRadiusAdjustment * 2;
    const bubbles = [];
    const bubbleColors = Object.keys(BUBBLE_COLORS);
    for (let i = 0; i < numBubbles; i += 1) {
      const diameter = bubbleSize + getRandomInt(-4, 4);
      const color = bubbleColors[i % bubbleColors.length];
      const { x, y } = getRandomCoordsOnEllipse(ellipseHeight, ellipseWidth);
      bubbles.push(
        <Bubble
          delay={i * delayFactor + delayOffset}
          diameter={diameter}
          initialColor={color}
          xOffset={x}
          yOffset={y}
          height={height}
          width={width}
          key={i}
        />,
      );
    }
    return bubbles;
  }
  render() {
    const dimensions = this.getSize();
    return (
      <Box {...dimensions} position="relative">
        {/* eslint-disable react/no-danger */}
        <style
          dangerouslySetInnerHTML={{
            __html: DELIGHTFUL_BUBBLES_KEYFRAMES,
          }}
        />
        {/* eslint-enable react/no-danger */}
        <div ref={(element) => (this.containerElement = element)}>
          {this.state.isInitialized &&
            this.createBubbles(dimensions.height || 0, dimensions.width || 0)}
          <Box position="relative">{this.props.children}</Box>
        </div>
      </Box>
    );
  }
}

export default DelightfulBubbles;
